// Professional painting contractor estimating engine.
// - Production-rate based (not "$X per room").
// - Fully data-driven via pricing_settings (DB row).
// - Produces realistic homeowner budget ranges, not binding quotes.

export type ProjectType =
  | "interior"
  | "exterior"
  | "single_room"
  | "multiple_rooms"
  | "cabinets"
  | "trim"
  | "doors"
  | "fence"
  | "deck"
  | "garage"
  | "commercial";

export const PROJECT_TYPE_LABELS: Record<ProjectType, string> = {
  interior: "Entire Interior",
  exterior: "Entire Exterior",
  single_room: "Single Room",
  multiple_rooms: "Multiple Rooms",
  cabinets: "Cabinets",
  trim: "Trim Only",
  doors: "Doors",
  fence: "Fence",
  deck: "Deck",
  garage: "Garage",
  commercial: "Commercial",
};

export type PaintQuality = "builder" | "premium" | "luxury";
export type SurfaceCondition = "excellent" | "good" | "fair" | "poor" | "heavy";
export type EstimateStatus = "new" | "contacted" | "scheduled" | "won" | "lost";

export const ROOM_TYPES = [
  "Bedroom", "Master Bedroom", "Living Room", "Family Room",
  "Kitchen", "Dining Room", "Bathroom", "Hallway", "Office",
  "Foyer", "Basement", "Stairwell",
] as const;

export type Surface =
  | "walls" | "ceiling" | "trim" | "doors" | "windows"
  | "baseboards" | "crown" | "cabinets";

export const SURFACE_LABELS: Record<Surface, string> = {
  walls: "Walls",
  ceiling: "Ceiling",
  trim: "Trim",
  doors: "Doors",
  windows: "Windows",
  baseboards: "Baseboards",
  crown: "Crown molding",
  cabinets: "Cabinets",
};

export type RoomSpec = {
  type?: string;
  length?: number | null;     // ft
  width?: number | null;      // ft
  ceilingHeight?: number | null; // ft
  floorSqft?: number | null;     // alternative to length×width
  doorCount?: number | null;
  windowCount?: number | null;
};

export type EstimateInput = {
  projectType: ProjectType;

  // Room-based projects
  rooms?: RoomSpec[];

  // Surfaces to include (applies to room/interior/exterior)
  surfaces?: Surface[];

  // Project-specific counts/measurements
  cabinetDoorDrawerCount?: number | null;
  trimLinearFeet?: number | null;
  doorCount?: number | null;
  fenceLinearFeet?: number | null;
  deckSqft?: number | null;
  exteriorSqft?: number | null;     // exterior siding area
  garageSqft?: number | null;       // garage floor sqft
  commercialSqft?: number | null;

  surfaceCondition?: SurfaceCondition;
  numberOfCoats?: 1 | 2 | 3;
  paintQuality?: PaintQuality;
};

export type EstimateResult = {
  gallons: number;
  laborHours: number;
  durationDays: number;
  priceLow: number;
  priceHigh: number;
  paintableWallSqft: number;
  paintableCeilingSqft: number;
  surfacesIncluded: Surface[];
  assumptions: string;
  lineItems: Array<{ label: string; low: number; high: number; detail?: string; category?: SpreadCategory }>;
};

export type SpreadCategory = "interior" | "exterior" | "cabinets" | "fence_deck";

export type PricingSettings = {
  wall_low: number; wall_high: number;
  ceiling_low: number; ceiling_high: number;
  trim_low: number; trim_high: number;
  baseboard_low: number; baseboard_high: number;
  crown_low: number; crown_high: number;
  door_low: number; door_high: number;
  window_low: number; window_high: number;
  cabinet_low: number; cabinet_high: number;
  exterior_low: number; exterior_high: number;
  fence_low: number; fence_high: number;
  deck_low: number; deck_high: number;
  garage_low: number; garage_high: number;
  commercial_low: number; commercial_high: number;

  quality_builder: number; quality_premium: number; quality_luxury: number;
  coats_one: number; coats_two: number; coats_three: number;
  condition_excellent: number; condition_good: number; condition_fair: number; condition_poor: number; condition_heavy: number;

  labor_markup: number; material_markup: number;
  coverage_per_gallon: number; production_sqft_per_hour: number;
  travel_fee: number; minimum_charge: number;

  // Range "confidence" spread per category. Mid = (raw_low + raw_high) / 2;
  // displayed low = mid * (1 - spread), displayed high = mid * (1 + spread).
  spread_interior: number;
  spread_exterior: number;
  spread_cabinets: number;
  spread_fence_deck: number;
};

export const DEFAULT_PRICING: PricingSettings = {
  wall_low: 1.5, wall_high: 3.5,
  ceiling_low: 1.0, ceiling_high: 2.5,
  trim_low: 2.0, trim_high: 5.0,
  baseboard_low: 1.5, baseboard_high: 4.0,
  crown_low: 2.0, crown_high: 6.0,
  door_low: 75, door_high: 200,
  window_low: 50, window_high: 150,
  cabinet_low: 80, cabinet_high: 200,
  exterior_low: 2.0, exterior_high: 7.0,
  fence_low: 3.0, fence_high: 8.0,
  deck_low: 3.0, deck_high: 7.0,
  garage_low: 1.5, garage_high: 3.5,
  commercial_low: 1.25, commercial_high: 3.5,

  quality_builder: 0.85, quality_premium: 1.0, quality_luxury: 1.3,
  coats_one: 0.75, coats_two: 1.0, coats_three: 1.3,
  condition_excellent: 0.95, condition_good: 1.0, condition_fair: 1.1, condition_poor: 1.25, condition_heavy: 1.45,

  labor_markup: 1.0, material_markup: 1.15,
  coverage_per_gallon: 350, production_sqft_per_hour: 180,
  travel_fee: 0, minimum_charge: 450,

  spread_interior: 0.25,
  spread_exterior: 0.35,
  spread_cabinets: 0.30,
  spread_fence_deck: 0.25,
};

const DEFAULT_DOOR_AREA = 21;   // sqft (≈ 3' × 7')
const DEFAULT_WINDOW_AREA = 15; // sqft

function roomAreas(room: RoomSpec) {
  const h = Math.max(7, room.ceilingHeight ?? 9);
  let floor = room.floorSqft ?? 0;
  let perimeter = 0;

  if (room.length && room.width) {
    floor = room.length * room.width;
    perimeter = 2 * (room.length + room.width);
  } else if (floor > 0) {
    // Assume square room when only sqft given.
    const side = Math.sqrt(floor);
    perimeter = 4 * side;
  }

  const doors = room.doorCount ?? 1;
  const windows = room.windowCount ?? 2;
  const grossWalls = perimeter * h;
  const netWalls = Math.max(0, grossWalls - doors * DEFAULT_DOOR_AREA - windows * DEFAULT_WINDOW_AREA);

  return { floor, perimeter, walls: netWalls, doors, windows };
}

function qualityMult(p: PricingSettings, q: PaintQuality) {
  return q === "builder" ? p.quality_builder : q === "luxury" ? p.quality_luxury : p.quality_premium;
}
function coatsMult(p: PricingSettings, c: number) {
  return c <= 1 ? p.coats_one : c >= 3 ? p.coats_three : p.coats_two;
}
function conditionMult(p: PricingSettings, c: SurfaceCondition) {
  return ({
    excellent: p.condition_excellent,
    good: p.condition_good,
    fair: p.condition_fair,
    poor: p.condition_poor,
    heavy: p.condition_heavy,
  } as const)[c];
}

function roundTo(n: number, step = 25) {
  return Math.round(n / step) * step;
}

export function computeEstimate(
  input: EstimateInput,
  pricing: PricingSettings = DEFAULT_PRICING,
): EstimateResult {
  const coats = (input.numberOfCoats ?? 2) as 1 | 2 | 3;
  const quality = input.paintQuality ?? "premium";
  const condition = input.surfaceCondition ?? "good";
  const qMult = qualityMult(pricing, quality);
  const cMult = coatsMult(pricing, coats);
  const condMult = conditionMult(pricing, condition);

  // Surfaces: default per project type
  let surfaces: Surface[] = input.surfaces ?? [];
  if (surfaces.length === 0) {
    if (input.projectType === "single_room" || input.projectType === "multiple_rooms" || input.projectType === "interior") {
      surfaces = ["walls"];
    } else if (input.projectType === "trim") surfaces = ["trim"];
    else if (input.projectType === "doors") surfaces = ["doors"];
    else if (input.projectType === "cabinets") surfaces = ["cabinets"];
  }
  const has = (s: Surface) => surfaces.includes(s);

  // Aggregate room dimensions
  const aggregate = (input.rooms ?? []).reduce((acc, r) => {
    const a = roomAreas(r);
    acc.walls += a.walls;
    acc.floor += a.floor;
    acc.perimeter += a.perimeter;
    acc.doors += a.doors;
    acc.windows += a.windows;
    return acc;
  }, { walls: 0, floor: 0, perimeter: 0, doors: 0, windows: 0 });

  const lineItems: EstimateResult["lineItems"] = [];
  let paintableWallSqft = 0;
  let paintableCeilingSqft = 0;

  const addLine = (label: string, lo: number, hi: number, category: SpreadCategory, detail?: string) => {
    if (hi <= 0) return;
    lineItems.push({ label, low: lo, high: hi, detail, category });
  };

  // ---- Room-based work (interior / single_room / multiple_rooms) ----
  if (["interior", "single_room", "multiple_rooms"].includes(input.projectType)) {
    if (has("walls") && aggregate.walls > 0) {
      paintableWallSqft = aggregate.walls;
      const lo = aggregate.walls * pricing.wall_low * cMult * condMult * qMult;
      const hi = aggregate.walls * pricing.wall_high * cMult * condMult * qMult;
      addLine("Walls", lo, hi, "interior", `${Math.round(aggregate.walls).toLocaleString()} sq ft`);
    }
    if (has("ceiling") && aggregate.floor > 0) {
      paintableCeilingSqft = aggregate.floor;
      const lo = aggregate.floor * pricing.ceiling_low * cMult * condMult;
      const hi = aggregate.floor * pricing.ceiling_high * cMult * condMult;
      addLine("Ceilings", lo, hi, "interior", `${Math.round(aggregate.floor).toLocaleString()} sq ft`);
    }
    if (has("trim") && aggregate.perimeter > 0) {
      const lo = aggregate.perimeter * pricing.trim_low * condMult;
      const hi = aggregate.perimeter * pricing.trim_high * condMult;
      addLine("Trim", lo, hi, "interior", `${Math.round(aggregate.perimeter)} lin ft`);
    }
    if (has("baseboards") && aggregate.perimeter > 0) {
      const lo = aggregate.perimeter * pricing.baseboard_low * condMult;
      const hi = aggregate.perimeter * pricing.baseboard_high * condMult;
      addLine("Baseboards", lo, hi, "interior", `${Math.round(aggregate.perimeter)} lin ft`);
    }
    if (has("crown") && aggregate.perimeter > 0) {
      const lo = aggregate.perimeter * pricing.crown_low * condMult;
      const hi = aggregate.perimeter * pricing.crown_high * condMult;
      addLine("Crown molding", lo, hi, "interior", `${Math.round(aggregate.perimeter)} lin ft`);
    }
    if (has("doors") && aggregate.doors > 0) {
      const lo = aggregate.doors * pricing.door_low;
      const hi = aggregate.doors * pricing.door_high;
      addLine("Doors", lo, hi, "interior", `${aggregate.doors} door${aggregate.doors > 1 ? "s" : ""}`);
    }
    if (has("windows") && aggregate.windows > 0) {
      const lo = aggregate.windows * pricing.window_low;
      const hi = aggregate.windows * pricing.window_high;
      addLine("Windows", lo, hi, "interior", `${aggregate.windows} window${aggregate.windows > 1 ? "s" : ""}`);
    }
    if (has("cabinets") && (input.cabinetDoorDrawerCount ?? 0) > 0) {
      const n = input.cabinetDoorDrawerCount!;
      addLine("Cabinets", n * pricing.cabinet_low * qMult, n * pricing.cabinet_high * qMult, "cabinets", `${n} doors/drawers`);
    }
  }

  // ---- Standalone scopes ----
  if (input.projectType === "cabinets" && (input.cabinetDoorDrawerCount ?? 0) > 0) {
    const n = input.cabinetDoorDrawerCount!;
    addLine("Cabinet refinishing", n * pricing.cabinet_low * qMult * condMult, n * pricing.cabinet_high * qMult * condMult, "cabinets", `${n} doors/drawers`);
  }
  if (input.projectType === "trim" && (input.trimLinearFeet ?? 0) > 0) {
    const lf = input.trimLinearFeet!;
    addLine("Trim", lf * pricing.trim_low * condMult, lf * pricing.trim_high * condMult, "interior", `${lf} lin ft`);
  }
  if (input.projectType === "doors" && (input.doorCount ?? 0) > 0) {
    const n = input.doorCount!;
    addLine("Doors", n * pricing.door_low * condMult, n * pricing.door_high * condMult, "interior", `${n} doors`);
  }
  if (input.projectType === "fence" && (input.fenceLinearFeet ?? 0) > 0) {
    const lf = input.fenceLinearFeet!;
    addLine("Fence", lf * pricing.fence_low * condMult, lf * pricing.fence_high * condMult, "fence_deck", `${lf} lin ft`);
    paintableWallSqft = lf * 6; // 6ft tall avg for paint volume estimate
  }
  if (input.projectType === "deck" && (input.deckSqft ?? 0) > 0) {
    const sf = input.deckSqft!;
    addLine("Deck", sf * pricing.deck_low * condMult, sf * pricing.deck_high * condMult, "fence_deck", `${sf} sq ft`);
    paintableWallSqft = sf;
  }
  if (input.projectType === "garage" && (input.garageSqft ?? 0) > 0) {
    const sf = input.garageSqft!;
    // garage walls ≈ perimeter * 8ft ≈ sf * 1.2
    const walls = sf * 1.2;
    paintableWallSqft = walls;
    addLine("Garage walls & ceiling", sf * pricing.garage_low * cMult * condMult, sf * pricing.garage_high * cMult * condMult, "exterior", `${sf} sq ft floor`);
  }
  if (input.projectType === "exterior" && (input.exteriorSqft ?? 0) > 0) {
    const sf = input.exteriorSqft!;
    paintableWallSqft = sf;
    addLine("Exterior siding", sf * pricing.exterior_low * cMult * condMult * qMult, sf * pricing.exterior_high * cMult * condMult * qMult, "exterior", `${sf} sq ft`);
    if (has("trim") && (input.trimLinearFeet ?? 0) > 0) {
      const lf = input.trimLinearFeet!;
      addLine("Exterior trim & fascia", lf * pricing.trim_low * condMult, lf * pricing.trim_high * condMult, "exterior", `${lf} lin ft`);
    }
    if (has("doors") && (input.doorCount ?? 0) > 0) {
      const n = input.doorCount!;
      addLine("Exterior doors", n * pricing.door_low, n * pricing.door_high, "exterior", `${n} doors`);
    }
  }
  if (input.projectType === "commercial" && (input.commercialSqft ?? 0) > 0) {
    const sf = input.commercialSqft!;
    paintableWallSqft = sf;
    addLine("Commercial space", sf * pricing.commercial_low * cMult * condMult, sf * pricing.commercial_high * cMult * condMult, "exterior", `${sf} sq ft`);
  }

  // Apply per-category midpoint + admin-controlled spread so the displayed
  // range reflects "confidence" instead of raw min/max rate combinations.
  const spreadFor = (cat: SpreadCategory | undefined) => {
    switch (cat) {
      case "exterior": return pricing.spread_exterior;
      case "cabinets": return pricing.spread_cabinets;
      case "fence_deck": return pricing.spread_fence_deck;
      case "interior":
      default: return pricing.spread_interior;
    }
  };
  for (const li of lineItems) {
    const mid = (li.low + li.high) / 2;
    const spread = Math.max(0, Math.min(0.9, spreadFor(li.category)));
    li.low = mid * (1 - spread);
    li.high = mid * (1 + spread);
  }

  let lowTotal = lineItems.reduce((s, l) => s + l.low, 0) * pricing.labor_markup;
  let highTotal = lineItems.reduce((s, l) => s + l.high, 0) * pricing.labor_markup;

  // Travel + minimum
  lowTotal += pricing.travel_fee;
  highTotal += pricing.travel_fee;
  lowTotal = Math.max(lowTotal, pricing.minimum_charge);
  highTotal = Math.max(highTotal, pricing.minimum_charge * 1.4);

  const priceLow = roundTo(lowTotal, 25);
  const priceHigh = roundTo(Math.max(highTotal, lowTotal * 1.15), 25);

  // Paint volume — walls + ceilings only contribute meaningfully.
  const paintableArea = paintableWallSqft + paintableCeilingSqft;
  const gallons = paintableArea > 0
    ? +((paintableArea * coats / pricing.coverage_per_gallon) * pricing.material_markup).toFixed(1)
    : 0;

  // Labor hours from production rate, scaled by condition (more prep).
  const laborHours = paintableArea > 0
    ? +((paintableArea * coats / pricing.production_sqft_per_hour) * condMult).toFixed(1)
    : Math.max(2, +((priceHigh / 75)).toFixed(1)); // fallback for count-based scopes

  const durationDays = Math.max(1, Math.ceil(laborHours / (2 * 8))); // crew of 2, 8h days

  const assumptions = [
    `Project type: ${PROJECT_TYPE_LABELS[input.projectType]}.`,
    surfaces.length ? `Surfaces: ${surfaces.map((s) => SURFACE_LABELS[s]).join(", ")}.` : null,
    `Paint quality: ${quality}. Coats: ${coats}. Surface condition: ${condition}.`,
    `Production rate ${pricing.production_sqft_per_hour} sq ft/hr per painter. Coverage ${pricing.coverage_per_gallon} sq ft/gal.`,
    `Preliminary budget range only — final pricing is confirmed during the on-site estimate.`,
  ].filter(Boolean).join(" ");

  // Scale + round line items so their sums exactly equal the displayed
  // priceLow / priceHigh totals (absorbs labor markup, travel, minimum,
  // and the roundTo(25) grand-total rounding).
  const rawLowSum = lineItems.reduce((s, l) => s + l.low, 0) || 1;
  const rawHighSum = lineItems.reduce((s, l) => s + l.high, 0) || 1;
  const lowScale = priceLow / rawLowSum;
  const highScale = priceHigh / rawHighSum;
  const scaled = lineItems.map((li) => ({
    ...li,
    low: li.low * lowScale,
    high: li.high * highScale,
  }));
  const rounded = scaled.map((li) => ({ ...li, low: roundTo(li.low, 5), high: roundTo(li.high, 5) }));
  // Absorb rounding residual into the largest line so sums match exactly.
  const fixResidual = (key: "low" | "high", target: number) => {
    const sum = rounded.reduce((s, l) => s + l[key], 0);
    const diff = target - sum;
    if (diff === 0 || rounded.length === 0) return;
    let idx = 0;
    for (let i = 1; i < rounded.length; i++) if (rounded[i][key] > rounded[idx][key]) idx = i;
    rounded[idx][key] += diff;
  };
  fixResidual("low", priceLow);
  fixResidual("high", priceHigh);

  return {
    gallons,
    laborHours,
    durationDays,
    priceLow,
    priceHigh,
    paintableWallSqft: Math.round(paintableWallSqft),
    paintableCeilingSqft: Math.round(paintableCeilingSqft),
    surfacesIncluded: surfaces,
    assumptions,
    lineItems: rounded,
  };
}